import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import classnames from 'classnames';

import {
  ProductCardDataLayerContainer,
} from '../../containers/ProductCardDataLayerContainer/ProductCardDataLayerContainer';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { ITEM_POSITION_SHIFT } from '../../hooks/productListHooks/productListHooks';
import { LayoutExtension } from '../../types/ResponsiveState';
import {
  ColorPresentational,
  EntityReferenceBlockWithHandPresentational,
  ImagePresentational,
  ProductCardType,
  ProductListItemPresentational,
} from '../../types/common';
import { ItemListID } from '../../types/dataLayer';
import { LayoutWrapper } from '../LayoutWrapper/LayoutWrapper';
import { LinkIntent, Link, LinkType, LinkIconPosition } from '../Link/Link';
import { Title } from '../Title/Title';
import { Typography } from '../Typography/Typography';
import styles from './MostChosenProducts.module.css';

const messages = defineMessages({
  title: {
    id: 'src.components.MostChosenProducts.title',
    defaultMessage: 'Most chosen',
  },
  showMore: {
    id: 'src.components.MostChosenProducts.showMore',
    defaultMessage: 'View all 200+ gift cards',
  },
});

export const NUMBER_TO_SHOW_TOP_PRODUCTS = 2;
export const NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_DESKTOP = 8;
export const NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_LAPTOP = 5;
export const NUMBER_TO_SHOW_TOP_BLOCKS_INITIALLY_ON_MOBILE = 4;

export const hooks = {
  useProducts(topProducts: EntityReferenceBlockWithHandPresentational[] | null = []) {
    return React.useMemo(
      () => topProducts ? topProducts.slice(0, NUMBER_TO_SHOW_TOP_PRODUCTS) : null,
      [topProducts],
    );
  },

  useMapProductsToDisplay(
    featuredProducts: EntityReferenceBlockWithHandPresentational[] | null = [],
    mostChosen: ProductListItemPresentational[] = [],
    layoutExtension: LayoutExtension,
  ): ProductsToDisplay[] {
    return React.useMemo(() => {
      let productList: ProductsToDisplay[] = [];

      const flaggedFeaturedProducts = featuredProducts?.map((product) => ({
        ...product,
        isFeatured: true,
      }));

      if (layoutExtension === LayoutExtension.DesktopLarge) {
        productList = [...(flaggedFeaturedProducts || []), ...mostChosen] as ProductsToDisplay[];
      } else {
        productList = [
          ...(flaggedFeaturedProducts?.slice(0, 1) || []),
          ...mostChosen,
          ...(flaggedFeaturedProducts?.slice(-1) || []),
        ] as ProductsToDisplay[];
      }

      return productList.filter(Boolean).map((product, idx) => {
        product.position = idx + ITEM_POSITION_SHIFT;
        return product;
      });
    }, [featuredProducts, layoutExtension, mostChosen]);
  },
};

type ProductsToDisplay = (
  | ProductListItemPresentational
  & EntityReferenceBlockWithHandPresentational
) & {
  isFeatured?: boolean;
  position: number;
};

export type Props = {
  layoutExtension: LayoutExtension;
  mostChosen: ProductListItemPresentational[];
  topProducts: EntityReferenceBlockWithHandPresentational[] | null;
};

export type MostChosenGiftcardProductsPresentational = {
  __typename?: string;
  backgroundColor?: ColorPresentational | null;
  description?: string | null;
  id: string | null;
  image?: ImagePresentational | null;
  path?: string | null;
  title?: string;
};

export const MostChosenProducts = React.memo(({
  mostChosen,
  topProducts,
  layoutExtension,
}: Props) => {
  const featuredProducts = hooks.useProducts(topProducts);
  const productsToDisplay = hooks.useMapProductsToDisplay(
    featuredProducts,
    mostChosen,
    layoutExtension,
  );

  const popRedirect = navigationHooks.usePOPRedirect();

  return (
    <LayoutWrapper>
      <div className={styles.mostChosenProducts}>
        <div className={styles.titleWrapper}>
          <Title
            title={<FormattedMessage {...messages.title} />}
            variant='h2'
            variantSize={layoutExtension ? 'm' : 's'}
          />
        </div>

        <div className={styles.products}>
          {productsToDisplay.map((product, idx) => (
            <div
              key={`most_chosen_${product.isFeatured ? 'featured_' : 'initial_block_'}${idx}`}
              className={classnames(styles.item, product.isFeatured ? styles.product : '')}
            >
              <ProductCardDataLayerContainer
                position={product.position}
                title={product.title}
                description={product.description}
                path={product.isFeatured ? product.referencedPath : product.path}
                cardImage={product.cardImage}
                backgroundImage={product.backgroundImage}
                backgroundColor={product.backgroundColor}
                showHand={product.showHand}
                className={classnames(styles.productCard, {
                  [styles.withHand]: product.showHand,
                })}
                id={product.isFeatured ? null : product.id}
                cardType={product.isFeatured ? ProductCardType.L : ProductCardType.S}
                isTopProduct={product.isFeatured}
                itemListID={ItemListID.MOST_CHOSEN_HOME_PAGE}
                showShadow={product.showShadow}
              />
            </div>
          ))}
        </div>

        <Link
          type={LinkType.Href}
          intent={LinkIntent.primary}
          to={popRedirect}
          iconPosition={LinkIconPosition.Right}
        >
          <Typography className={styles.showMore} variant='bodySubText1'>
            <FormattedMessage {...messages.showMore} />
          </Typography>
        </Link>
      </div>
    </LayoutWrapper>
  );
});

MostChosenProducts.displayName = 'MostChosenProducts';
